// SteffiAvatar.jsx
import React from "react";
import { IoMusicalNotesOutline } from "react-icons/io5";
import { PiMusicNoteSimpleLight } from "react-icons/pi";

export default function SteffiAvatar({
  mouth = "neutral",      // "smile" | "neutral" | "talk1" | "talk2" | "talk3"
  eyes = "open",          // "open" | "closed"
  eyebrows = "none",      // "none" | "neutral" | "raised" | "angry" | "sad" | "surprised"
  extra = "none",         // "sleep" | "none"
  src = "/steffi_orange_ani.png",
  className = "w-64",
  debug = false,
  faceRect = { left: 21.5, top: 11.8, width: 57, height: 57 },
}) {
  const boxStyle = {
    left: `${faceRect.left}%`,
    top: `${faceRect.top}%`,
    width: `${faceRect.width}%`,
    height: `${faceRect.height}%`,
  };
  const drawSleepZs = (count) => {
    const color = "#FFA31A";
    const zs = [
      { x: 40, y: 30, size: 8, rot: -6 },
      { x: 48, y: 22, size: 10, rot: -10 },
      { x: 56, y: 15, size: 12, rot: -13 },
    ];
    return (
      <g>
        {zs.slice(0, Math.max(0, Math.min(count, zs.length))).map(({ x, y, size, rot }, i) => (
          <text
            key={i}
            x={x}
            y={y}
            fontSize={size}
            fill={color}
            fontWeight="700"
            textAnchor="middle"
            transform={`rotate(${rot} ${x} ${y})`}
          >
            Z
          </text>
        ))}
      </g>
    );
  };

  // Smile reference
  const smileY = 56;
  const smileDepth = 8;

  const renderEyebrows = () => {
    const color = "#FFA31A";
    const w = 2; // stroke width

    switch (eyebrows) {
      case "neutral":
        // subtle short arcs slightly above eyes
        return (
          <>
            <path d="M27 33 C 32 31, 38 31, 43 33" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
            <path d="M57 33 C 62 31, 68 31, 73 33" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
          </>
        );

      case "raised":
        // higher and more curved
        return (
          <>
            <path d="M26 30 C 32 27, 38 27, 44 30" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
            <path d="M56 30 C 62 27, 68 27, 74 30" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
          </>
        );

      case "angry":
        // angled inward (\/) lower
        return (
          <>
            <path d="M28 34 L 42 31" stroke={color} strokeWidth={w} strokeLinecap="round" />
            <path d="M72 34 L 58 31" stroke={color} strokeWidth={w} strokeLinecap="round" />
          </>
        );

      case "sad":
        // angled outward (/ \) lower
        return (
          <>
            <path d="M32 31 L 28 34" stroke={color} strokeWidth={w} strokeLinecap="round" />
            <path d="M68 31 L 72 34" stroke={color} strokeWidth={w} strokeLinecap="round" />
          </>
        );

      case "surprised":
        // high shallow arcs
        return (
          <>
            <path d="M26 28 C 34 26, 36 26, 44 28" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
            <path d="M56 28 C 64 26, 66 26, 74 28" stroke={color} strokeWidth={w} strokeLinecap="round" fill="none" />
          </>
        );

     case "straight":
      // flat horizontal eyebrows above eyes (moved up)
      return (
        <>
          <line
            x1="28"
            y1="26"   // was 34 → moved up
            x2="42"
            y2="22"   // was 30 → moved up
            stroke={color}
            strokeWidth={w}
            strokeLinecap="round"
          />
          <line
            x1="58"
            y1="22"   // was 30 → moved up
            x2="72"
            y2="26"   // was 34 → moved up
            stroke={color}
            strokeWidth={w}
            strokeLinecap="round"
          />
        </>
      );

     case "straight_up":
      // flat horizontal eyebrows above eyes (raised higher)
      return (
        <>
          <line
            x1="28"
            y1="22"   // was 26 → moved up
            x2="42"
            y2="18"   // was 22 → moved up
            stroke={color}
            strokeWidth={w}
            strokeLinecap="round"
          />
          <line
            x1="58"
            y1="18"   // was 22 → moved up
            x2="72"
            y2="22"   // was 26 → moved up
            stroke={color}
            strokeWidth={w}
            strokeLinecap="round"
          />
        </>
      );

      default:
      case "none":
        return null;
    }
    
  };

  // Utility to render eyes
  const renderEyes = () => {
    if (eyes === "closed") {
      return (
        <>
          <path d="M28 42 C 32 44, 38 44, 42 42" stroke="#FFA31A" strokeWidth="2" strokeLinecap="round" fill="none" />
          <path d="M58 42 C 62 44, 68 44, 72 42" stroke="#FFA31A" strokeWidth="2" strokeLinecap="round" fill="none" />
        </>
      );
    }
    if (eyes === "open") {
      return (
        <>
          <circle cx="35" cy="38" r="6" fill="#FFA31A" />
          <circle cx="65" cy="38" r="6" fill="#FFA31A" />
        </>
      );
    }
    if (eyes === "happy") {
      return (
        <>
          {/* Happy arc eyes */}
          <path
            d="M28 40 A10 10 0 0 1 42 40"
            stroke="#FFA31A"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M58 40 A10 10 0 0 1 72 40"
            stroke="#FFA31A"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </>
      );
    }
    return null;
  };
  


  // Utility to render mouth
  const renderMouth = () => {
    switch (mouth) {
      case "smile":
        return (
          <path
            d={`M35 ${smileY} C 45 ${smileY + smileDepth}, 55 ${smileY + smileDepth}, 65 ${smileY}`}
            stroke="#FFA31A"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        );
      case "neutral":
        return (
          <line
            x1="40"
            y1="60"
            x2="60"
            y2="60"
            stroke="#FFA31A"
            strokeWidth="3"
            strokeLinecap="round"
          />
        );
      case "talk1":
        return <rect x="40" y="58" width="20" height="6" rx="3" fill="#FFA31A" />;
      case "talk2":
        return <rect x="38" y="56" width="24" height="12" rx="6" fill="#FFA31A" />;
      case "talk3":
        return <rect x="35" y="52" width="30" height="20" rx="10" fill="#FFA31A" />;
      case "oval":
        return (
          <ellipse
            cx="50"
            cy="60"
            rx="6"
            ry="4"
            fill="none"
            stroke="#FFA31A"
            strokeWidth="3"
          />
        );
      default:
        return null;
    }
  };

  // Utility to render extras
  const renderExtra = () => {
   if (extra === "sleep1") {
    return drawSleepZs(1);
  }
  if (extra === "sleep2") {
    return drawSleepZs(2);
  }
  if (extra === "sleep3") {
    return drawSleepZs(3);
  }

  // (optional) keep old "sleep" for backward-compat → show full stack
  if (extra === "sleep") {
    return drawSleepZs(3);
  }
        if (extra === "music") {
          return (
            <g>
              <g transform="translate(50 45) scale(0.20)">
                <IoMusicalNotesOutline
                  size={90}
                  color="#FFA31A"
                  style={{ display: "block" }}
                />
              </g>

              <g transform="translate(25 50) scale(0.20)">
                <PiMusicNoteSimpleLight
                  size={90}
                  color="#FFA31A"
                  style={{ display: "block" }}
                />
              </g>
            </g>
          );
        }

        if (extra === "music2") {
          return (
            <g>
              <g transform="translate(26 50) scale(0.20)">
                <IoMusicalNotesOutline
                  size={90}
                  color="#FFA31A"
                  style={{ display: "block" }}
                />
              </g>

              <g transform="translate(45 55) scale(0.20)">
                <PiMusicNoteSimpleLight
                  size={90}
                  color="#FFA31A"
                  style={{ display: "block" }}
                />
              </g>
            </g>
          );
        }
    return null;
  };

  return (
    <div className={`relative ${className} aspect-square select-none`}>
      <img
        src={src}
        alt="STEFFI device"
        className="absolute inset-0 h-full w-full object-contain"
        draggable={false}
      />

      {/* FACE OVERLAY */}
      <div className="absolute inset-0">
        <div className="absolute" style={boxStyle}>
          {debug && (
            <div className="absolute inset-0">
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
                <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(0,255,0,0.6)" strokeWidth="1.5" />
                <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(0,255,0,0.35)" strokeWidth="1" />
                <line x1="50" y1="0" x2="50" y2="100" stroke="rgba(0,255,0,0.35)" strokeWidth="1" />
              </svg>
              <div className="absolute -top-6 left-0 text-xs bg-black/60 text-white px-1 rounded">
                {`left:${faceRect.left}% top:${faceRect.top}% w:${faceRect.width}% h:${faceRect.height}%`}
              </div>
            </div>
          )}

          <svg viewBox="0 0 100 100" className="h-full w-full">
            <defs>
              <clipPath id="face-clip">
                <circle cx="50" cy="50" r="48" />
              </clipPath>
            </defs>
            <g clipPath="url(#face-clip)">
              {renderEyebrows()}
              {renderEyes()}
              {renderMouth()}
              {renderExtra()}
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}